import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { decryptData } from '../../utils/storageCrypto';
const DebitCoinsModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    consumer_id: '',
    coins_amount: '',
    transaction_category: 'manual_debit',
    transaction_description: '',
    reason: 'adjustment'
  });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerWallet, setSelectedCustomerWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.consumer_id) {
      fetchCustomerWallet(formData.consumer_id);
    }
  }, [formData.consumer_id]);

  const fetchCustomers = async () => {
    try {
      const encryptedToken = localStorage.getItem('token');
      const token = decryptData(encryptedToken);
      const response = await fetch('/api/customers', {
        headers: {
         'x-token': token
        }
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomerWallet = async (customerId) => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      const response = await fetch(`/api/buildcoins/wallets/${customerId}`, {
        headers: {
          'x-token': token
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedCustomerWallet(data.data.wallet);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.consumer_id) newErrors.consumer_id = 'Customer is required';
    if (!formData.coins_amount || formData.coins_amount <= 0) {
      newErrors.coins_amount = 'Valid coin amount is required';
    }
    if (selectedCustomerWallet && parseInt(formData.coins_amount) > selectedCustomerWallet.available_balance) {
      newErrors.coins_amount = 'Amount exceeds available balance';
    }
    if (!formData.transaction_description) {
      newErrors.transaction_description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        coins_amount: parseInt(formData.coins_amount)
      });
      
      // Reset form
      setFormData({
        consumer_id: '',
        coins_amount: '',
        transaction_category: 'manual_debit',
        transaction_description: '',
        reason: 'adjustment'
      });
      setSelectedCustomerWallet(null);
      setErrors({});
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Debit BuildCoins</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Customer Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer *
            </label>
            <select
              value={formData.consumer_id}
              onChange={(e) => setFormData({ ...formData, consumer_id: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.consumer_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer._id} value={customer._id}>
                  {customer.customer_name} - {customer.email}
                </option>
              ))}
            </select>
            {errors.consumer_id && (
              <p className="mt-1 text-sm text-red-600">{errors.consumer_id}</p>
            )}
          </div>

          {/* Wallet Info */}
          {selectedCustomerWallet && (
            <div className="mb-4 p-3 bg-blue-50 rounded-md">
              <div className="text-sm text-blue-800">
                <p><strong>Available Balance:</strong> {selectedCustomerWallet.available_balance.toLocaleString()} coins</p>
                <p><strong>Locked Balance:</strong> {selectedCustomerWallet.locked_balance.toLocaleString()} coins</p>
              </div>
            </div>
          )}

          {/* Coins Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coins Amount *
            </label>
            <input
              type="number"
              min="1"
              max={selectedCustomerWallet?.available_balance || undefined}
              value={formData.coins_amount}
              onChange={(e) => setFormData({ ...formData, coins_amount: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.coins_amount ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter coins amount"
            />
            {errors.coins_amount && (
              <p className="mt-1 text-sm text-red-600">{errors.coins_amount}</p>
            )}
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason
            </label>
            <select
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="adjustment">Balance Adjustment</option>
              <option value="correction">Error Correction</option>
              <option value="penalty">Penalty</option>
              <option value="refund_reversal">Refund Reversal</option>
              <option value="system_maintenance">System Maintenance</option>
            </select>
          </div>

          {/* Transaction Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={formData.transaction_category}
              onChange={(e) => setFormData({ ...formData, transaction_category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="manual_debit">Manual Debit</option>
              <option value="system_adjustment">System Adjustment</option>
              <option value="penalty_deduction">Penalty Deduction</option>
              <option value="error_correction">Error Correction</option>
              <option value="refund_reversal">Refund Reversal</option>
            </select>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.transaction_description}
              onChange={(e) => setFormData({ ...formData, transaction_description: e.target.value })}
              rows="3"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.transaction_description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter transaction description"
            />
            {errors.transaction_description && (
              <p className="mt-1 text-sm text-red-600">{errors.transaction_description}</p>
            )}
          </div>

          {/* Warning */}
          <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This action will permanently debit coins from the customer's wallet. 
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Debit Coins'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DebitCoinsModal;
